/**
 * Service middleware for the backend `notification-log` statistics API.
 *
 * Docs: http://repo.shelly.cloud:6040/docs/#/statistics?id=notification-log
 * Backend: https://gitlab.allterco.net/Shelly/be/iot-backend/-/work_items/2022
 * Front-end: FE-1715
 *
 * Both endpoints are served on the `cloud` client (Bearer JWT is attached via
 * `handleToken`). The feature is currently enabled only for a whitelist of
 * accounts on the NB/REPO environments — for any other account the GET returns
 * an error which we normalize to an empty list, so calling it is always safe.
 */

import { getReq, postReq } from './http';
import type { NotificationLogEntry, NotificationLogState } from 'src/types/NotificationLog';

const NOTIFICATION_LOG_URL = 'v2/statistics/notification-log';
const NOTIFICATION_LOG_UPDATE_STATE_URL = 'v2/statistics/notification-log/update-state';

/** Request timeout for the notification-log calls, matching the event-log service. */
const REQUEST_TIMEOUT = 20000;

/** Type guard for a well-formed notification-log entry returned by the backend. */
const isNotificationLogEntry = (value: unknown): value is NotificationLogEntry => {
	return !!value && typeof (value as NotificationLogEntry).id === 'number';
};

/**
 * Retrieve all available notifications for the authenticated user.
 * GET `v2/statistics/notification-log`
 *
 * @returns The notifications (newest-first, up to the 100-item FIFO cap), or an
 * empty array when the feature is disabled for the account or the call fails.
 */
export async function fetchNotificationLog(): Promise<NotificationLogEntry[]> {
	const res = await getReq(NOTIFICATION_LOG_URL, {}, REQUEST_TIMEOUT);
	return Array.isArray(res) ? (res as NotificationLogEntry[]) : [];
}

/**
 * Update the state of one or more notifications (unseen/seen/dismissed).
 * POST `v2/statistics/notification-log/update-state`
 *
 * @param rowIds - Non-empty array of notification ids.
 * @param state - The new state.
 * @returns The updated entries echoed by the backend, or `undefined` on failure.
 */
export async function updateNotificationState(
	rowIds: number[],
	state: NotificationLogState,
): Promise<NotificationLogEntry[] | undefined> {
	if (rowIds.length === 0) return [];

	const res = await postReq(
		NOTIFICATION_LOG_UPDATE_STATE_URL,
		{ rowIds, state },
		REQUEST_TIMEOUT,
		'',
		true,
		true,
	);

	return Array.isArray(res) && res.every(isNotificationLogEntry)
		? (res as NotificationLogEntry[])
		: undefined;
}
